/* Standaard e-mailvelden — overal dezelfde lay-out: template → onderwerp → WYSIWYG →
 * tags → (cc/bcc) → (bijlagen). Zie /app/docs/COMMUNICATION.md */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor, toEditorHtml } from "@/components/RichTextEditor";
import { RecipientField } from "@/components/RecipientField";
import { TagChips, TemplatePicker, AttachmentsField } from "./commShared";
import MailGallery from "./MailGallery";
import { X } from "lucide-react";

export default function EmailFields({
  subject, setSubject, body, setBody,
  tags = [], templates = true, personalTemplates = false, fillTemplate, onTemplateApplied,
  cc, setCc, bcc, setBcc,                       // cc/bcc alleen tonen wanneer setters meegegeven
  attachments, setAttachments,                  // bijlagen alleen tonen wanneer setter meegegeven
  extraPeople = [],                             // extra deelnemers uit het dossier → snelkeuze-chips
  extraTo, setExtraTo,                          // extra ontvangers (Aan) — alleen met setter
  toChip, minHeight = 180, enableVideo = true, placeholder = "Typ je bericht…",
  subjectPlaceholder = "Onderwerp", testId = "comm-email", children,
}) {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const withCc = typeof setCc === "function";
  useEffect(() => {
    if (!withCc) return;
    api.get("/team").then((r) => setTeam((r.data || []).filter((m) => m.active !== false && m.email && m.email !== user?.email))).catch(() => {});
  }, [withCc, user?.email]);

  const applyTemplate = (t) => {
    const fill = fillTemplate || ((x) => x || "");
    if (t.subject || t.name) setSubject(fill(t.subject || t.name));
    setBody(toEditorHtml(fill(t.body)));
    if (withCc && (t.cc || []).length) { setCc(t.cc); setShowCc(true); }
    if (typeof setBcc === "function" && (t.bcc || []).length) { setBcc(t.bcc); setShowBcc(true); }
    if (typeof setAttachments === "function" && (t.attachments || []).length) {
      setAttachments([...(attachments || []), ...t.attachments]);
    }
    onTemplateApplied?.(t);
  };
  const ccVisible = withCc && (showCc || (cc || []).length > 0);
  const bccVisible = typeof setBcc === "function" && (showBcc || (bcc || []).length > 0);
  const withExtraTo = typeof setExtraTo === "function";
  const toQuickAdd = withExtraTo
    ? extraPeople.filter((p) => p.email && !(extraTo || []).includes(p.email.toLowerCase()))
    : [];

  return (
    <div className="space-y-3">
      {(toChip || withCc) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {toChip && (<><span className="text-slate-500">Aan:</span>
            <span className="rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-xs text-slate-700" data-testid={`${testId}-to-chip`}>{toChip}</span></>)}
          {withExtraTo && (extraTo || []).map((em) => (
            <span key={em} data-testid={`${testId}-to-extra-${em}`}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs text-emerald-700">
              {em}
              <button type="button" onClick={() => setExtraTo(extraTo.filter((x) => x !== em))} className="text-emerald-400 hover:text-red-500"><X className="h-3 w-3" /></button>
            </span>
          ))}
          {toQuickAdd.map((p) => (
            <button key={p.email} type="button"
              onClick={() => setExtraTo([...(extraTo || []), p.email.toLowerCase()])}
              data-testid={`${testId}-to-person-${p.email}`} title={`${p.name || p.email} — extra deelnemer toevoegen als ontvanger`}
              className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700 hover:bg-emerald-100 transition-colors">
              + {(p.name || p.email).split(" ")[0]}
            </button>
          ))}
          {withCc && !ccVisible && <button type="button" onClick={() => setShowCc(true)} className="text-xs text-blue-600 hover:underline" data-testid={`${testId}-show-cc`}>+ Cc</button>}
          {typeof setBcc === "function" && !bccVisible && <button type="button" onClick={() => setShowBcc(true)} className="text-xs text-blue-600 hover:underline" data-testid={`${testId}-show-bcc`}>+ Bcc</button>}
        </div>
      )}
      {ccVisible && <RecipientField label="Cc" value={cc} setValue={setCc} team={team} people={extraPeople} testId={`${testId}-cc`} />}
      {bccVisible && <RecipientField label="Bcc" value={bcc} setValue={setBcc} team={team} people={extraPeople} testId={`${testId}-bcc`} />}
      {templates && (
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <TemplatePicker kind="email" personal={personalTemplates} onPick={applyTemplate} testId={`${testId}-template-select`} />
          </div>
          <MailGallery onPick={applyTemplate} testId={`${testId}-gallery`} />
        </div>
      )}
      {children}
      <div className="space-y-1.5">
        <Label>Onderwerp *</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder={subjectPlaceholder} data-testid={`${testId}-subject`} />
      </div>
      <div className="space-y-1.5">
        <Label>Bericht *</Label>
        <RichTextEditor testId={`${testId}-body`} value={toEditorHtml(body)} onChange={setBody}
          placeholder={placeholder} minHeight={minHeight} enableVideo={enableVideo} />
        <TagChips tags={tags} onInsert={(t) => setBody((body || "") + t)} testId={`${testId}-tags`} />
      </div>
      {typeof setAttachments === "function" && (
        <AttachmentsField value={attachments} setValue={setAttachments} testId={`${testId}-attach`} />
      )}
    </div>
  );
}
